'use client';

import React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { Box } from '../box';

export interface PopoverProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  modal?: boolean;
  children: React.ReactNode;
}

interface PopoverComponent extends React.FC<PopoverProps> {
  Root: typeof Popover;
  Trigger: typeof PopoverTrigger;
  Content: typeof PopoverContent;
  Anchor: typeof PopoverAnchor;
  Close: typeof PopoverClose;
  Positioner: React.FC<{ children: React.ReactNode }>;
}

export const Popover: PopoverComponent = ({
  open,
  onOpenChange,
  defaultOpen,
  modal = true,
  children,
}) => {
  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={onOpenChange}
      defaultOpen={defaultOpen}
      modal={modal}
    >
      {children}
    </PopoverPrimitive.Root>
  );
};

export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;
export const PopoverClose = PopoverPrimitive.Close;

export interface PopoverContentProps extends PopoverPrimitive.PopoverContentProps {
  width?: string | number;
  maxHeight?: string | number;
  bg?: string;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: string;
  borderRadius?: string;
  shadow?: string;
  boxShadow?: string;
}

export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(
  (
    {
      children,
      width = 'auto',
      maxHeight,
      align = 'start',
      sideOffset = 4,
      bg,
      backgroundColor,
      borderColor,
      borderWidth,
      borderRadius,
      shadow,
      boxShadow,
      ...props
    },
    ref
  ) => {
    return (
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={ref}
          align={align}
          sideOffset={sideOffset}
          {...props}
          style={{
            width,
            maxHeight,
            ...props.style,
          }}
        >
          <Box
            backgroundColor={bg || backgroundColor || 'white'}
            borderRadius={borderRadius || 'lg'}
            boxShadow={shadow || boxShadow || 'lg'}
            border="1px solid"
            borderColor={borderColor || '#E5E7EB'}
            borderWidth={borderWidth}
            zIndex={1500} // popover level - must be above modal (1400)
            pointerEvents="auto" // Ensure clicks work inside modal dialogs
            {...((props as { 'data-state'?: string })['data-state']
              ? { 'data-state': (props as { 'data-state'?: string })['data-state'] }
              : {})}
            _open={{
              animation: 'fadeIn 0.15s ease-out',
            }}
            _closed={{
              animation: 'fadeOut 0.15s ease-in',
            }}
          >
            {children}
          </Box>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    );
  }
);

PopoverContent.displayName = 'PopoverContent';

// Add dot notation support
Popover.Root = Popover;
Popover.Trigger = PopoverTrigger;
Popover.Content = PopoverContent;
Popover.Anchor = PopoverAnchor;
Popover.Close = PopoverClose;
Popover.Positioner = React.Fragment; // Radix UI doesn't have Positioner, use Fragment

export default Popover;
